import { type ReactNode, type RefObject, useEffect, useId, useRef, useState } from 'react';

import {
  ApiError,
  contentPath,
  fetchHeadlessPlanning,
  type HeadlessPlanning,
  startHeadlessPlanning,
  stopHeadlessPlanning,
} from '../api.ts';
import { DASHBOARD_POLL_MS, describeError } from '../data.tsx';
import { Icon } from '../Icon.tsx';
import { Link } from '../router.tsx';
import { useToast } from '../toast.tsx';
import { Loader, Markdown, Notice } from '../ui.tsx';
import * as wording from '../wording.ts';
import { Decisions } from './Decisions.tsx';

/**
 * The headless planning conversation (US-023): the planner's latest message
 * and a box to answer it, so a content gets planned without the terminal or
 * `mc-admin plan`. Reads `GET /api/contents/:id/planning/headless` every 3 s
 * while shown; sends with `POST`, stops with `DELETE`.
 *
 * The drawer of a pending content shows it from the start (`whenIdle="offer"`,
 * with **Plan with Claude here**); the content page only once a conversation
 * exists (`whenIdle="hide"`), since its terminal is the way in there. A server
 * without Docker answers 503: the drawer says so in a line, the page shows
 * nothing.
 */
export function PlanningConversation({
  contentId,
  canStart,
  whenIdle,
  focusReply,
  onMarkReady,
  onChanged,
}: {
  readonly contentId: string;
  /** Whether there is a container to plan in: setup finished and did not fail. */
  readonly canStart: boolean;
  readonly whenIdle: 'offer' | 'hide';
  /**
   * Bumped to scroll the panel into view and focus the reply box (the card's
   * **Answer Claude**). Acted on once the first reading has drawn the box.
   */
  readonly focusReply?: number;
  /** Opens MarkReadyDialog. */
  readonly onMarkReady: () => void;
  /** The conversation moved on: the card line and the plan state follow. */
  readonly onChanged?: () => void;
}) {
  const toast = useToast();
  const replyId = useId();
  const sectionRef = useRef<HTMLElement>(null);
  const replyRef = useRef<HTMLTextAreaElement>(null);
  const [view, setView] = useState<HeadlessPlanning | null>(null);
  /** 503 `headless_planning_unavailable`: no Docker, so no headless planner at all. */
  const [unavailable, setUnavailable] = useState<string | null>(null);
  /** 409 `planning_terminal_open`: the terminal on the content page holds the plan. */
  const [terminalOpen, setTerminalOpen] = useState(false);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const onChangedRef = useRef(onChanged);
  onChangedRef.current = onChanged;
  const lastStatus = useRef<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    const load = (): void => {
      fetchHeadlessPlanning(contentId, controller.signal)
        .then((next) => {
          setView(next);
          setUnavailable(null);
        })
        .catch((cause: unknown) => {
          if (controller.signal.aborted) return;
          if (cause instanceof ApiError && cause.code === 'headless_planning_unavailable') {
            setUnavailable(describeError(cause));
          }
          // Anything else: the next beat tries again, and the panel keeps its last reading.
        });
    };
    load();
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') load();
    }, DASHBOARD_POLL_MS);
    return () => {
      controller.abort();
      window.clearInterval(timer);
    };
  }, [contentId]);

  // An exchange that ends changes the card line and maybe the plan: read the board again.
  useEffect(() => {
    if (view === null) return;
    const previous = lastStatus.current;
    lastStatus.current = view.status;
    if (previous === 'running' && view.status !== 'running') onChangedRef.current?.();
  }, [view]);

  // **Answer Claude**: once the first reading is drawn, bring the box into view and focus it.
  const focusPending = useRef<number | undefined>(undefined);
  useEffect(() => {
    focusPending.current = focusReply;
  }, [focusReply]);
  const ready = view !== null;
  useEffect(() => {
    if (focusPending.current === undefined || !ready) return;
    focusPending.current = undefined;
    window.requestAnimationFrame(() => {
      const box = replyRef.current;
      (box ?? sectionRef.current)?.scrollIntoView({ block: 'center', behavior: 'smooth' });
      box?.focus({ preventScroll: true });
    });
  }, [focusReply, ready]);

  const send = async (message: string | undefined): Promise<void> => {
    setBusy(true);
    setTerminalOpen(false);
    try {
      setView(await startHeadlessPlanning(contentId, message));
      if (message !== undefined) setDraft('');
      onChangedRef.current?.();
    } catch (cause: unknown) {
      if (cause instanceof ApiError && cause.code === 'planning_terminal_open') setTerminalOpen(true);
      else toast.error(describeError(cause));
    } finally {
      setBusy(false);
    }
  };

  const stop = async (): Promise<void> => {
    setBusy(true);
    try {
      setView(await stopHeadlessPlanning(contentId));
      toast.ok(wording.PLANNING_STOPPED);
      onChangedRef.current?.();
    } catch (cause: unknown) {
      toast.error(describeError(cause));
    } finally {
      setBusy(false);
    }
  };

  if (unavailable !== null) {
    if (whenIdle === 'hide') return null;
    return (
      <Section sectionRef={sectionRef}>
        <p className="muted">{unavailable}</p>
      </Section>
    );
  }
  if (view === null) return null;
  if (view.status === 'idle' && whenIdle === 'hide') return null;

  const running = view.status === 'running';
  const terminalNotice = terminalOpen && (
    <Notice kind="warn">
      {wording.PLANNING_TERMINAL_OPEN}{' '}
      <Link className="link" href={contentPath(contentId)}>
        {wording.PLANNING_TERMINAL_LINK}
      </Link>
    </Notice>
  );

  if (view.status === 'idle') {
    return (
      <Section sectionRef={sectionRef}>
        <p className="muted">{canStart ? wording.PLANNING_HERE_HINT : wording.PLANNING_NO_CONTAINER}</p>
        {terminalNotice}
        <div className="board-drawer__actions">
          <button
            type="button"
            className="button"
            disabled={busy || !canStart}
            onClick={() => void send(undefined)}
          >
            <Icon name="comment" />
            {wording.PLANNING_HERE}
          </button>
        </div>
      </Section>
    );
  }

  const message = draft.trim();

  return (
    <Section sectionRef={sectionRef} meta={wording.planningExchange(view.exchange, view.costUsd, view.totalCostUsd)}>
      {view.autopilot ? (
        // Autopilot (US-021): the planner asked nothing, so what it decided stands in for a question.
        !running && <Decisions decisions={view.decisions} whenEmpty="say" />
      ) : view.reply === null || view.reply.trim() === '' ? (
        !running && <p className="muted">{wording.PLANNING_NO_REPLY}</p>
      ) : (
        <div className="planning-conversation__reply">
          <Markdown text={view.reply} />
        </div>
      )}
      {view.status === 'failed' && view.error !== null && <Notice kind="error">{view.error}</Notice>}
      {running && (
        <div className="board-drawer__actions">
          <Loader size="sm" label={wording.PLANNING_THINKING} />
          <button type="button" className="button button--quiet" disabled={busy} onClick={() => void stop()}>
            <Icon name="stop" />
            {wording.PLANNING_STOP}
          </button>
        </div>
      )}
      {view.planParses && !running && (
        <div className="board-drawer__actions">
          <p className="field__hint">{wording.PLANNING_PLAN_PARSES}</p>
          <button type="button" className="button button--primary" onClick={onMarkReady}>
            <Icon name="check" />
            {wording.ACTION_MARK_READY}
          </button>
        </div>
      )}
      {terminalNotice}
      <label className="field__label" htmlFor={replyId}>
        {view.autopilot ? wording.PLANNING_CHANGE : wording.PLANNING_REPLY}
      </label>
      <textarea
        ref={replyRef}
        id={replyId}
        className="field__input"
        rows={4}
        value={draft}
        onChange={(event) => {
          setDraft(event.target.value);
        }}
        onKeyDown={(event) => {
          if (event.key !== 'Enter' || !(event.ctrlKey || event.metaKey)) return;
          event.preventDefault();
          if (message !== '' && !running && !busy) void send(message);
        }}
      />
      <p className="field__hint">{view.autopilot ? wording.PLANNING_CHANGE_HINT : wording.PLANNING_REPLY_HINT}</p>
      <div className="board-drawer__actions">
        <button
          type="button"
          className="button button--primary"
          disabled={busy || running || message === ''}
          onClick={() => void send(message)}
        >
          <Icon name="comment" />
          {wording.PLANNING_SEND}
        </button>
        {!view.autopilot && (
          <button type="button" className="button" disabled={busy || running} onClick={() => void send('ok')}>
            <Icon name="check" />
            {wording.PLANNING_TAKE_ALL}
          </button>
        )}
      </div>
    </Section>
  );
}

function Section({
  sectionRef,
  meta,
  children,
}: {
  readonly sectionRef: RefObject<HTMLElement | null>;
  readonly meta?: string;
  readonly children: ReactNode;
}) {
  return (
    <section ref={sectionRef} className="board-drawer__section planning-conversation" id="planning-conversation">
      <h3 className="board-drawer__section-title">
        {wording.PLANNING_CONVERSATION}
        {meta !== undefined && <span className="planning-conversation__meta">{meta}</span>}
      </h3>
      {children}
    </section>
  );
}
